import React, { useState, useCallback, createContext, useContext, useMemo } from 'react';
import enTranslations from '../languages/en';
import itTranslations from '../languages/it';

interface TranslationContextType {
  t: (key: string, variables?: Record<string, string | number>) => string;
  changeLanguage: (lng: 'en' | 'it') => void;
  currentLanguage: 'en' | 'it';
}

interface TranslationKeys {
  [key: string]: string; // Assuming individual translation files are string-to-string maps
}

interface AllTranslations {
  en: TranslationKeys;
  it: TranslationKeys;
}

const TranslationContext = createContext<TranslationContextType | undefined>(undefined);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Determine the initial language based on localStorage, then browser's language setting
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'it'>(() => {
    // Check localStorage first
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage === 'en' || savedLanguage === 'it') {
      return savedLanguage;
    }
    // Fall back to browser language
    const browserLanguage = navigator.language.toLowerCase();
    return browserLanguage.startsWith('it') ? 'it' : 'en';
  });

  const allTranslations = useMemo<AllTranslations>(() => ({
    en: enTranslations,
    it: itTranslations,
  }), []);

  const t = useCallback((key: string, variables: Record<string, string | number> = {}): string => {
    const translation = allTranslations[currentLanguage]?.[key] || key;

    return Object.entries(variables).reduce(
      (message, [name, value]) => message.replaceAll(`{{${name}}}`, String(value)),
      translation,
    );
  }, [currentLanguage, allTranslations]);

  const changeLanguage = (lng: 'en' | 'it') => {
    setCurrentLanguage(lng);
    // Persist language preference to localStorage
    localStorage.setItem('preferredLanguage', lng);
  };

  return (
    <TranslationContext.Provider value={{ t, changeLanguage, currentLanguage }}>
      {children}
    </TranslationContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
};
